import React, {
  useState,
} from 'react';
import {
  injectStripe,
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
} from 'react-stripe-elements';
import * as Yup from 'yup';
import {
  Formik,
  Field,
} from 'formik';
import {
  useDispatch,
} from 'react-redux';
import {
  toast,
} from 'react-toastify';
import {
  useNavigate
} from 'react-router-dom';
import MobileDetect from 'mobile-detect';

import {
  coreActions,
} from '@gymcloud/core';

import {
  data,
  zipRegex,
} from 'web-utils';
import {
  TextField,
  SelectField,
  CheckboxField,
  PasswordField,
  FormElementField,
} from 'web-components';
import {
  Flex,
  Text,
} from 'web-styled';

import * as S from '../styled';

const countryZipRequiredList = ['us', 'ca'];
const requiredReplacement = {
  cardExpiryIsValid: 'Expiration date is required',
  CVCIsValid: 'CVC is required',
  state: 'State is required',
  zip: 'Zip is required',
};

const validationSchema = () => Yup.lazy(
  values => Yup.object().shape({
    first_name: Yup.string()
      .required('First Name is required'),
    last_name: Yup.string()
      .required('Last Name is required'),
    email: Yup.string()
      .email('Email must be a valid email')
      .required('Email is required'),
    password: Yup.string()
      .min(6)
      .required('Password is required'),
    password_confirmation: Yup.string()
      .required('Confirm password is required')
      .oneOf([Yup.ref('password'), null], 'Passwords do not match'),

    cardNumberIsValid: Yup.mixed()
      .oneOf([true, null], 'Please enter a valid card number')
      .required('Credit card is required'),
    cardExpiryIsValid: Yup.mixed()
      .oneOf([true, null], 'Please enter a valid card expiration date')
      .required('Required'),
    CVCIsValid: Yup.mixed()
      .oneOf([true, null], 'Please enter a valid CVC')
      .required('Required'),
    cardHolderName: Yup.string()
      .required('Name is required'),
    ...(countryZipRequiredList.indexOf(values.country) !== -1
      ? {
        state: Yup.string()
          .required('Required'),
        zip: Yup.string()
          .matches(
            zipRegex[values.country],
            {
              message: 'Please enter a valid zip code.',
              excludeEmptyString: true,
            },
          )
          .required('Required'),
      }
      : {}
    ),

    agreement: Yup.bool().nullable(true)
      .oneOf([null, true], 'Must Accept Terms and Conditions'),

  }),
);

type Props = {
  stripe: any,
  elements: any,
};

const CheckoutForm = ({
  stripe,
  elements,
}: Props) => {
  const [savedPM, setSavedPM] = useState(null);
  const [savedClient, setSavedClient] = useState(null);
  const [uniqEmail, setUniqEmail] = useState<string | null>(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const md = new MobileDetect(window.navigator.userAgent);

  function checkEmail(email: string) {
    return new Promise((resolve) => {
      if (uniqEmail === email) {
        resolve(false);
      } else {
        dispatch(
          coreActions.checkEmail({
            email,
            callbacks: {
              onError: () => {
                setUniqEmail(null);
                resolve(true);
              },
              onSuccess: () => {
                setUniqEmail(email);
                resolve(false);
              },
            },
          }),
        );
      }
    });
  }

  function createClient(values) {
    return new Promise((resolve, reject) => {
      if (savedClient) {
        resolve(savedClient);
      } else {
        dispatch(
          coreActions.requestCreateUser({
            data: {
              user: values,
              profile: {
                first_name: values.first_name,
                last_name: values.last_name,
                country: values.country,
                state: values.state,
                zip: values.zip,
              },
            },
            callbacks: {
              onSuccess: (res: any) => {
                setSavedClient(res);
                resolve(res);
              },
              onError: reject,
            },
          }),
        );
      }
    });
  }

  function createPaymentMethod({
    name,
    country,
    state,
    zip,
  }) {
    return new Promise((resolve, reject) => {
      if (savedPM) {
        resolve(savedPM);
      } else {
        stripe
          .createPaymentMethod({
            type: 'card',
            card: elements.getElement('cardNumber'),
            billing_details: {
              name,
              address: countryZipRequiredList.indexOf(country) !== -1
                ? {
                  state,
                  country,
                  postal_code: zip,
                }
                : {
                  country,
                },
            },
          })
          .then((result: any) => {
            if (result.error) {
              reject(result.error);
            } else {
              dispatch(
                coreActions.requestCreatePaymentMethod({
                  body: {
                    id: result.paymentMethod.id,
                  },
                  callbacks: {
                    onSuccess: (res: any) => {
                      setSavedPM(res);
                      resolve(res);
                    },
                    onError: reject,
                  },
                }),
              );
            }
          });
      }
    });
  }

  function upgradeAccount() {
    return new Promise((resolve, reject) => {
      dispatch(
        coreActions.requestUpgradeAccount({
          endpoint: 'hts',
          callbacks: {
            onSuccess: resolve,
            onError: reject,
          },
        }),
      );
    });
  }

  function finishBuyProduct(paymentIntentId: number) {
    return new Promise((resolve, reject) => {
      dispatch(
        coreActions.requestFinishBuy({
          paymentIntentId,
          callbacks: {
            onSuccess: resolve,
            onError: reject,
          },
        }),
      );
    });
  }

  function finishLogin(username: string, password: string) {
    return new Promise((resolve, reject) => {
      dispatch(
        coreActions.loginRequest({
          payload: {
            username,
            password,
          },
          redirectTo: '/hts-setup',
          callbacks: {
            onSuccess: resolve,
            onError: reject,
          },
        }),
      );
    });
  }

  const handleSubmit = async (values: any) => {
    const hasEmail = await checkEmail(values.email);
    if (hasEmail) {
      toast.error(
        'Email already exists in GymCloud database. Use a different email or contact GymCloud support.',
      );
      return false;
    }

    await createClient(values);

    const paymentMethod: any = await createPaymentMethod({
      name: values.cardHolderName,
      zip: values.zip,
      state: values.state,
      country: values.country,
    });

    const paymentIntent: any = await upgradeAccount();
    const { error } = await stripe.confirmCardPayment(
      paymentIntent.client_secret,
      {
        payment_method: paymentMethod.id,
      },
    );

    if (error) {
      throw error;
    }

    await finishBuyProduct(paymentIntent.id);
    toast.success('Payment successful!');
    if (md.mobile()) {
      navigate('/hts-setup');
    } else {
      await finishLogin(values.email, values.password);
    }

    return true;
  };

  return (
    <Formik
      initialValues={{
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        password_confirmation: '',
        namespace: 'gymcloud',
        is_pro: true,
        is_client: false,

        cardNumberIsValid: null,
        cardExpiryIsValid: null,
        CVCIsValid: null,
        cardHolderName: '',
        country: 'us',
        zip: '',
        state: '',

        agreement: false,
      }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {(formikProps) => {
        const location = {
          ca: {
            label: 'Province*',
            placeholder: 'Select your province',
            zipPlaceholder: 'A1A 1A1',
            options: data.caStatesOptions,
          },
          us: {
            label: 'State*',
            placeholder: 'Select your state',
            zipPlaceholder: '12345',
            options: data.usStatesOptions,
          },
        }[formikProps.values.country];

        return (
          <S.Form column onSubmit={formikProps.handleSubmit}>
            <Flex column width="100%" spacing={15}>
              <S.SubTitle>
                <Text size="34px" color="#333333">
                  Account Information
                </Text>
              </S.SubTitle>
              <Field
                type="text"
                name="first_name"
                label="First Name*"
                color="#121827"
                component={TextField}
                placeholder="e.g. John"
              />
              <Field
                type="text"
                name="last_name"
                label="Last Name*"
                color="#121827"
                component={TextField}
                placeholder="e.g Smith"
              />
              <Field
                type="email"
                name="email"
                label="Email*"
                color="#121827"
                component={TextField}
                placeholder="Enter email"
              />
              <Field
                name="password"
                label="Password*"
                color="#121827"
                component={PasswordField}
                placeholder="Password"
                autoComplete="off"
              />
              <Field
                name="password_confirmation"
                label="Confirm Password*"
                color="#121827"
                component={PasswordField}
                placeholder="Password"
                autoComplete="off"
              />

              <S.SubTitle>
                <Text size="34px" color="#333333">
                  Payment Information
                </Text>
              </S.SubTitle>
              <Field
                type="text"
                name="cardNumberIsValid"
                label="Card Number*"
                color="#121827"
                Element={CardNumberElement}
                component={FormElementField}
              />
              <Flex
                spacing={15}
                width="100%"
                justify="space-between"
              >
                <Field
                  type="text"
                  name="cardExpiryIsValid"
                  label="Expiration Date*"
                  color="#121827"
                  width="100px"
                  className="card-expiry"
                  errorStyle={{
                    margin: 0,
                    alignSelf: 'flex-start',
                  }}
                  Element={CardExpiryElement}
                  component={FormElementField}
                />
                <Field
                  type="text"
                  name="CVCIsValid"
                  label="CVC*"
                  color="#121827"
                  width="80px"
                  className="card-cvc"
                  errorStyle={{
                    margin: 0,
                    textAlign: 'right',
                  }}
                  Element={CardCvcElement}
                  component={FormElementField}
                />
              </Flex>
              <Field
                type="text"
                name="cardHolderName"
                label="Cardholder Name*"
                color="#121827"
                component={TextField}
                placeholder="Enter Name"
                autoComplete="off"
              />
              <Field
                name="country"
                label="Country*"
                color="#121827"
                options={data.countriesOptions}
                component={SelectField}
                onChange={() => {
                  formikProps.setFieldValue('zip', '');
                  formikProps.setFieldValue('state', '');
                }}
                maxMenuHeight={500}
                placeholder="-- Select Country --"
                theme={{
                  borderRadius: 5,
                }}
                styles={{
                  option: provided => ({
                    ...provided,
                    fontSize: '12px',
                    lineHeight: '12px',
                    fontWeight: 500,
                  }),
                  valueContainer: provided => ({
                    ...provided,
                    color: '#495057',
                    fontSize: '1rem',
                    lineHeight: '1.5',
                    fontWeight: 500,
                  }),
                }}
              />
              {countryZipRequiredList.indexOf(formikProps.values.country) !== -1 && (
                <S.Location>
                  <Field
                    name="state"
                    label={location.label}
                    color="#121827"
                    options={location.options}
                    component={SelectField}
                    maxMenuHeight={500}
                    placeholder={location.placeholder}
                    theme={{
                      borderRadius: 5,
                    }}
                    styles={{
                      option: provided => ({
                        ...provided,
                        fontSize: '12px',
                        lineHeight: '12px',
                        fontWeight: 500,
                      }),
                      valueContainer: provided => ({
                        ...provided,
                        color: '#495057',
                        fontSize: '1rem',
                        lineHeight: '1.5',
                        fontWeight: 500,
                      }),
                    }}
                  />
                  <Field
                    type="text"
                    name="zip"
                    label="Zip*"
                    color="#121827"
                    component={TextField}
                    placeholder={location.zipPlaceholder}
                    autoComplete="off"
                  />
                </S.Location>
              )}

              <Flex column width="100%">
                <S.SubTitle>
                  <Text size="34px" color="#333333">
                    Order Summary
                  </Text>
                </S.SubTitle>
                <Flex width="100%" column spacing={15}>
                  <Flex
                    width="100%"
                    padding="15px"
                    background="#e9e9e9"
                    justify="space-between"
                  >
                    <Text>
                      Hybrid Training System Course
                    </Text>
                    <Text>
                      $299 (USD)
                    </Text>
                  </Flex>
                  <Flex
                    width="100%"
                    padding="15px"
                    background="#e9e9e9"
                    justify="space-between"
                  >
                    <Text>
                      <b>Total</b>
                    </Text>
                    <Text>
                      <b>$299 (USD)</b>
                    </Text>
                  </Flex>
                </Flex>
              </Flex>

              <Field
                name="agreement"
                label={(
                  <span>
                    {'I Agree with the '}
                    <a
                      href="https://www.gymcloud.com/legal"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      GymCloud End User Agreement
                    </a>
                  </span>
                )}
                component={CheckboxField}
              />

              <S.SubmitButton
                isLoading={formikProps.isSubmitting}
                isDisabled={formikProps.isSubmitting}
                onClick={() => {
                  formikProps.validateForm().then(errors => {
                    Object.keys(errors)
                      .reverse()
                      .forEach(key => {
                        const error = errors[key] === 'Required'
                          ? requiredReplacement[key]
                          : errors[key];
                        toast.error(error);
                      });
                  });
                }}
              >
                Complete My Order
              </S.SubmitButton>
            </Flex>
          </S.Form>
        );
      }}
    </Formik>
  );
};

export default injectStripe(CheckoutForm);
